import React, { FC, useMemo, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDndMonitor,
  pointerWithin,
  closestCenter,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';

import Switch from '@mui/material/Switch';
import AddIcon from '@mui/icons-material/Add';
import GridViewIcon from '@mui/icons-material/GridView';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import { addNotification } from '../../../store';
import { StatItem } from './StatItem';
import { NgButton, NgTooltip } from '../../../components';
import { StyledInput } from './StyledInput';
import {
  deepCloneSections,
  getAtPath,
  insertAtPath,
  isAncestorOrSelf,
  removeAtPath,
  shiftPathAfterRemoval,
  subtreeHeight,
  updateAtPath,
} from './outlineTreeUtils';
import { buttonVariants } from '@clatter/ui';

const StyledOutlineSection = styled.div<{ $nestOver?: boolean; $readOnly?: boolean }>`
  container-type: inline-size;
  /* border-bottom: ${({ $readOnly, theme }) => ($readOnly ? 'none' : `1px solid ${theme.palette.border}`)}; */
  padding: 0;
  border-radius: 4px;
  transition: outline 0.1s ease, background-color 0.1s ease;
  cursor: pointer;

  ${({ $nestOver }) =>
    $nestOver &&
    `
    outline: 2px solid #2196f3;
    background-color: rgba(33, 150, 243, 0.03);
  `}
`;

const StyledSectionHeader = styled.div<{
  $active?: boolean;
  $readOnly?: boolean;
  $depth?: number;
  $compact?: boolean;
  $hasChildren?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme, $compact, $hasChildren }) =>
    `${$compact && $hasChildren ? '2px' : '4px'} ${theme.spacing.small}`};
  padding-left: ${({ $readOnly, $depth, theme }) =>
    $readOnly ? `calc(${($depth ?? 0) * 24}px + ${theme.spacing.small})` : undefined};
  cursor: pointer;
  user-select: none;
  border-radius: 4px;
  background-color: ${({ $active, theme }) => ($active ? theme.palette.accent : 'transparent')};

  .left {
    display: flex;
    align-items: center;
    gap: 1px;
    font-size: ${({ theme }) => theme.fontSize.body};
    font-weight: 600;
  }

  .right {
    display: ${({ $compact }) => ($compact ? 'none' : 'flex')};
    align-items: center;
    gap: 1px;

    /* display:none; */
    svg {
      font-size: 18px;
      color: ${({ theme }) => theme.palette.darkGrey};
      cursor: pointer;

      &:hover {
        color: ${({ theme }) => theme.palette.black};
      }
    }
  }

  @container (max-width: 320px) {
    flex-wrap: wrap;
    padding-bottom: 6px;

    .left {
      width: 100%;
    }
    .right {
      width: 100%;
      padding-bottom: 4px;
      justify-content: flex-end;
    }
  }
`;

const StyledChevron = styled(ChevronRightIcon)<{ $expanded: boolean }>`
  font-size: 18px;
  transition: transform 0.2s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? '90deg' : '0deg')});
`;

const StyledItemList = styled.div<{ $readOnly?: boolean }>`
  cursor: pointer;
  padding: ${({ $readOnly, theme }) => ($readOnly ? '2px 0 0 0' : `4px 0 4px ${theme.spacing.medium}`)};
`;

const StyledSlideItem = styled.div<{ $selected?: boolean; $dropBefore?: boolean; $dropAfter?: boolean }>`
  font-size: 14px;
  background-color: ${({ $selected, theme }) => ($selected ? theme.palette.accent : 'transparent')};
  border-radius: 4px;
  margin: 2px 0;
  padding: 6px 8px;
  position: relative;

  &:hover .slide-actions {
    opacity: 1;
  }

  ${({ $dropBefore }) =>
    $dropBefore &&
    `
    &::before {
      content: '';
      position: absolute;
      top: -1px;
      left: 0;
      right: 0;
      height: 2px;
      background: #2196f3;
      border-radius: 2px;
      box-shadow: 0 0 6px rgba(33, 150, 243, 0.5);
      z-index: 10;
    }
  `}

  ${({ $dropAfter }) =>
    $dropAfter &&
    `
    &::after {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      height: 2px;
      background: #2196f3;
      border-radius: 2px;
      box-shadow: 0 0 6px rgba(33, 150, 243, 0.5);
      z-index: 10;
    }
  `}
`;

const StyledSlideHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;
`;

const Title = styled.span`
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
`;

const SlideDetails = styled.div`
  padding-left: 18px;
  padding-top: 4px;
  padding-bottom: 4px;
`;

const SlideNotes = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.palette.darkGrey};
  opacity: 0.8;
  margin-bottom: 4px;

  span {
    font-weight: 600;
  }
`;

const SlideSearchQuery = styled.div`
  font-size: 12px;
  font-family: monospace;
  color: ${({ theme }) => theme.palette.darkGrey};
  opacity: 0.6;

  span {
    font-weight: 600;
  }
`;

const SlideEditInput = styled.input`
  font-size: 13px;
  border: 1px solid ${({ theme }) => theme.palette.border};
  border-radius: 4px;
  padding: 6px 10px;
  width: 100%;
  outline: none;
  margin-top: 4px;

  &:focus {
    border-color: #7c3aed;
  }
`;

const SlideEditRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;

  label {
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.palette.darkGrey};
  }
`;

const SlideEditActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const SlideHeaderActions = styled.span`
  margin-left: auto;
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.15s ease;

  svg {
    font-size: ${({ theme }) => theme.fontSize.lg};
    color: ${({ theme }) => theme.palette.darkGrey};
    cursor: pointer;
  }
`;

const StyledEmptyDropZone = styled.div<{ $isOver: boolean }>`
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed ${({ $isOver, theme }) => ($isOver ? '#2196f3' : theme.palette.border)};
  border-radius: ${({ theme }) => theme.radius.card};
  margin: 4px 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.palette.darkGrey};
  background-color: ${({ $isOver }) => ($isOver ? 'rgba(33, 150, 243, 0.05)' : 'transparent')};
  transition: border-color 0.15s ease, background-color 0.15s ease;
`;

const EmptySectionDropZone: FC<{ id: string; data: GapData }> = ({ id, data }) => {
  const { setNodeRef, isOver } = useDroppable({ id, data });
  return (
    <StyledEmptyDropZone ref={setNodeRef as React.Ref<HTMLDivElement>} $isOver={isOver}>
      Drop slide here
    </StyledEmptyDropZone>
  );
};

const StyledAddSectionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => `${theme.spacing.small} / 2`};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.small};
  margin-top: 8px;
  border: 1px dashed ${({ theme }) => theme.palette.border};
  border-radius: ${({ theme }) => theme.radius.normal};
  background: none;
  color: ${({ theme }) => theme.palette.darkGrey};
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.palette.background};
  }
`;

const StyledDragHandle = styled.span`
  display: flex;
  align-items: center;
  cursor: grab;
  color: ${({ theme }) => theme.palette.darkGrey};
  opacity: 0.35;
  flex-shrink: 0;
  touch-action: none;

  &:hover {
    opacity: 0.7;
  }
  &:active {
    cursor: grabbing;
  }

  svg {
    font-size: ${({ theme }) => theme.fontSize.default};
  }
`;

const StyledDropGap = styled.div<{ $isOver: boolean; $isActive: boolean }>`
  position: relative;
  height: ${({ $isOver, $isActive }) => ($isOver ? '0.75rem' : $isActive ? '4px' : '2px')};
  margin: ${({ $isOver }) => ($isOver ? '0.125rem 0' : '0')};
  z-index: 100;

  ${({ $isActive }) =>
    $isActive &&
    `
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 0;
      right: 0;
      height: 16px;
    }
  `}

  ${({ $isOver }) =>
    $isOver &&
    `
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 0;
      right: 0;
      height: 3px;
      background-color: #2196f3;
      border-radius: 2px;
      box-shadow: 0 0 8px rgba(33, 150, 243, 0.5);
    }
  `}
`;

const StyledDragOverlayBox = styled.div`
  border-radius: ${({ theme }) => theme.radius.card};
  border: 1px solid ${({ theme }) => theme.palette.border};
  color: ${({ theme }) => theme.palette.darkGrey};
  background: ${({ theme }) => theme.palette.white};
  padding: ${({ theme }) => `${theme.spacing.small} ${theme.spacing.medium}`};
  font-size: ${({ theme }) => theme.fontSize.default};
  font-weight: 500;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  cursor: grabbing;
  max-width: 280px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

type SectionDragData = { type: 'section'; path: number[] };
type SectionNestData = { type: 'sec-nest'; path: number[] };
type SlideDragData = { type: 'slide'; parentPath: number[]; itemIdx: number };
type GapData = { type: 'gap'; parentPath: number[]; itemIdx: number };
type GallerySlideData = { type: 'gallery-slide'; slideData: unknown };
type SlideTargetData = { type: 'slide-target'; parentPath: number[]; itemIdx: number };
type DnDData = SectionDragData | SectionNestData | SlideDragData | GapData | GallerySlideData | SlideTargetData;

interface OutlineSlideSelectData {
  event: React.MouseEvent;
  slide: OutlineSlide;
  sectionPath: number[];
  itemIdx: number;
}

interface OutlineCtxValue {
  sections: PresentationOutlineSection[];
  maxDepth: number;
  sectionLabel: string;
  isActiveSectionDrag: boolean;
  isActiveSlideDrag: boolean;
  isExternalDrag: boolean;
  activePath: number[] | null;
  activeSectionSubtreeHeight: number;

  expandedSections: Set<string>;
  expandedSlides: Set<string>;
  onToggleSection: (path: number[]) => void;
  onToggleSlide: (key: string) => void;

  editingPath: string | null;
  editValue: string;
  setEditValue: (v: string) => void;
  onStartEditingSection: (path: number[], name: string) => void;
  onCommitEditSection: () => void;
  onCancelEditSection: () => void;
  onRemoveSection: (path: number[]) => void;
  onRemoveSlide: (sectionPath: number[], itemIdx: number) => void;
  onToggleEnabled: (path: number[]) => void;

  editingSlide: string | null;
  editSlideNotes: string;
  editSlideQuery: string;
  setEditSlideNotes: (v: string) => void;
  setEditSlideQuery: (v: string) => void;
  onStartEditingSlide: (key: string, notes: string, query: string) => void;
  onCommitSlideEdit: (sectionPath: number[], itemIdx: number) => void;
  onCancelEditingSlide: () => void;

  selectedSlides: OutlineSlide[];
  activeSectionPath?: number[] | null;
  onSlideSelect?: (data: OutlineSlideSelectData) => void;
  onSectionSelect?: (path: number[], section: PresentationOutlineSection) => void;
  isSlideBeingDragged: (parentPath: number[], itemIdx: number) => boolean;
  readOnly?: boolean;
  hideSlides?: boolean;
  hideReadingTime?: boolean;
  hideSlideCount?: boolean;
  compact?: boolean;
  showSectionToggle?: boolean;
  slideDropTarget: { overItemId: string; side: 'before' | 'after' } | null;
}

const OutlineCtx = createContext<OutlineCtxValue>(null!);

const DropGap: FC<{ id: string; data: GapData; isActive: boolean }> = ({ id, data, isActive }) => {
  const { setNodeRef, isOver } = useDroppable({ id, data });
  return <StyledDropGap ref={setNodeRef} $isOver={isOver && isActive} $isActive={isActive} />;
};

const SlideNode: FC<{ slide: OutlineSlide; parentPath: number[]; itemIdx: number }> = ({
  slide,
  parentPath,
  itemIdx,
}) => {
  const ctx = useContext(OutlineCtx);
  const slideKey = `${parentPath.join('-')}-i${itemIdx}`;
  const targetId = `slide-target:${parentPath.join(',')}:${itemIdx}`;

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: `slide-drag:${parentPath.join(',')}:${itemIdx}`,
    data: { type: 'slide', parentPath, itemIdx } satisfies SlideDragData,
  });

  const { setNodeRef: setDropRef } = useDroppable({
    id: targetId,
    data: { type: 'slide-target', parentPath, itemIdx } satisfies SlideTargetData,
    disabled: ctx.readOnly,
  });

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      setDragRef(el as any);
      setDropRef(el as any);
    },
    [setDragRef, setDropRef],
  );

  const isSlideExpanded = ctx.expandedSlides.has(slideKey);
  const isEditingThisSlide = ctx.editingSlide === slideKey;
  const isSelected = ctx.selectedSlides.some((selectedSlide) => selectedSlide.instanceId === slide.instanceId);
  const isDimmed = isDragging || ctx.isSlideBeingDragged(parentPath, itemIdx);
  const hasDetails = slide.purpose || slide.query;
  const dropIndicator = ctx.slideDropTarget?.overItemId === targetId ? ctx.slideDropTarget.side : null;

  return (
    <StyledSlideItem
      ref={setRef}
      $selected={isSelected}
      $dropBefore={dropIndicator === 'before'}
      $dropAfter={dropIndicator === 'after'}
      style={{ opacity: isDimmed ? 0.4 : 1 }}
      onClick={(e) => ctx.onSlideSelect?.({ event: e, slide, sectionPath: parentPath, itemIdx })}
    >
      <StyledSlideHeader>
        {!ctx.readOnly && (
          <StyledDragHandle {...attributes} {...listeners} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <DragIndicatorIcon />
          </StyledDragHandle>
        )}
        {slide.Title}
        {!ctx.readOnly && (
          <SlideHeaderActions className="slide-actions">
            {ctx.onRemoveSlide && (
              <NgTooltip title="Remove Slide" placement="top">
                <DeleteOutlineIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    ctx.onRemoveSlide(parentPath, itemIdx);
                  }}
                />
              </NgTooltip>
            )}
          </SlideHeaderActions>
        )}
      </StyledSlideHeader>

      {isSlideExpanded && (
        <SlideDetails>
          {isEditingThisSlide ? (
            <>
              <SlideEditRow>
                <label>Notes:</label>
                <SlideEditInput
                  name="slide-notes"
                  autoComplete="on"
                  value={ctx.editSlideNotes}
                  onChange={(e) => ctx.setEditSlideNotes(e.target.value)}
                  placeholder="Add notes for this slide..."
                />
              </SlideEditRow>
              <SlideEditRow>
                <label>Query:</label>
                <SlideEditInput
                  name="slide-query"
                  autoComplete="on"
                  value={ctx.editSlideQuery}
                  onChange={(e) => ctx.setEditSlideQuery(e.target.value)}
                  placeholder="Search query for matching slides..."
                />
              </SlideEditRow>
              <SlideEditActions>
                <NgButton variant={buttonVariants.primary} onClick={() => ctx.onCommitSlideEdit(parentPath, itemIdx)}>
                  Save
                </NgButton>
                <NgButton variant={buttonVariants.clear} onClick={ctx.onCancelEditingSlide}>
                  Cancel
                </NgButton>
              </SlideEditActions>
            </>
          ) : hasDetails ? (
            <>
              {slide.purpose && (
                <SlideNotes>
                  <span>Notes:</span> {slide.purpose}
                </SlideNotes>
              )}
              {slide.query && (
                <SlideSearchQuery>
                  <span>Query:</span> {slide.query}
                </SlideSearchQuery>
              )}
            </>
          ) : (
            <SlideNotes style={{ opacity: 0.5, fontStyle: 'italic' }}>No notes or query. Click edit to add.</SlideNotes>
          )}
        </SlideDetails>
      )}
    </StyledSlideItem>
  );
};

const SectionNode: FC<{ section: PresentationOutlineSection; path: number[]; depth: number }> = ({
  section,
  path,
  depth,
}) => {
  const ctx = useContext(OutlineCtx);
  const pathStr = path.join('-');

  const isLocked = section.locked === true;

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: `sec-drag:${path.join(',')}`,
    data: { type: 'section', path } satisfies SectionDragData,
    disabled: isLocked,
  });

  const nestDisabled = depth + 2 + ctx.activeSectionSubtreeHeight > ctx.maxDepth || isLocked;
  const { setNodeRef: setNestRef, isOver: isNestOver } = useDroppable({
    id: `sec-nest:${path.join(',')}`,
    data: { type: 'sec-nest', path } satisfies SectionNestData,
    disabled: nestDisabled,
  });

  const setRef = useCallback((el: HTMLDivElement | null) => {
    setDragRef(el);
    setNestRef(el);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const expanded = ctx.expandedSections.has(pathStr);
  const isEditing = ctx.editingPath === pathStr;
  const disabled = section.enabled === false;
  const isActive = ctx.activeSectionPath?.join('-') === pathStr;
  const items = section.items ?? [];
  const showContent = expanded || section.headerless;

  const itemGapActive =
    ctx.isExternalDrag ||
    ctx.isActiveSlideDrag ||
    (ctx.isActiveSectionDrag && depth + 2 + ctx.activeSectionSubtreeHeight <= ctx.maxDepth);

  const slideCount = useMemo(() => {
    const countSlides = (section: PresentationOutlineSection): number =>
      (section.items ?? []).reduce((sum, item) => sum + (item.kind === 'slide' ? 1 : countSlides(item.data)), 0);

    return countSlides(section);
  }, [section.items]);

  const hasSubsections = items.some((i) => i.kind === 'section');

  return (
    <StyledOutlineSection
      ref={setRef}
      $nestOver={isNestOver}
      $readOnly={ctx.readOnly}
      style={{ opacity: isDragging ? 0.4 : disabled ? 0.5 : 1 }}
    >
      {!section.headerless && (
        <StyledSectionHeader
          $active={isActive}
          $readOnly={ctx.readOnly}
          $depth={depth}
          $compact={ctx.compact}
          $hasChildren={hasSubsections}
          onClick={() => {
            if (!isEditing) {
              ctx.onSectionSelect?.(path, section);
            }
          }}
        >
          <span className="left">
            {!ctx.readOnly && !isLocked && (
              <StyledDragHandle {...attributes} {...listeners} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <DragIndicatorIcon />
              </StyledDragHandle>
            )}
            {(ctx.readOnly || ctx.hideSlides ? hasSubsections : items.length > 0) && (
              <StyledChevron
                $expanded={expanded}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  if (!isEditing) ctx.onToggleSection(path);
                }}
              />
            )}
            {isEditing ? (
              <StyledInput
                autoFocus
                value={ctx.editValue}
                onChange={(e) => ctx.setEditValue(e.target.value)}
                onBlur={ctx.onCommitEditSection}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') ctx.onCommitEditSection();
                  if (e.key === 'Escape') ctx.onCancelEditSection();
                }}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <Title>{section.displayName || section.title}</Title>
            )}
          </span>
          <span className="right">
            {!ctx.hideSlideCount && (
              <StatItem>
                <GridViewIcon /> {slideCount}
              </StatItem>
            )}
            {!ctx.hideReadingTime && (
              <StatItem>
                <AccessTimeIcon /> {slideCount * 2} min
              </StatItem>
            )}
            {!ctx.readOnly && !isLocked && (
              <>
                {ctx.showSectionToggle && (
                  <NgTooltip title={`Enable ${ctx.sectionLabel}`} placement="top">
                    <Switch
                      size="small"
                      checked={section.enabled !== false}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => ctx.onToggleEnabled(path)}
                    />
                  </NgTooltip>
                )}
                <NgTooltip title={`Edit ${ctx.sectionLabel} Name`} placement="top">
                  <EditOutlinedIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      ctx.onStartEditingSection(path, section.displayName || section.title || '');
                    }}
                  />
                </NgTooltip>
                <NgTooltip title={`Remove ${ctx.sectionLabel}`} placement="top">
                  <DeleteOutlineIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      ctx.onRemoveSection(path);
                    }}
                  />
                </NgTooltip>
              </>
            )}
          </span>
        </StyledSectionHeader>
      )}

      {showContent && items.length === 0 && !ctx.readOnly && !ctx.hideSlides && (
        <StyledItemList>
          <EmptySectionDropZone id={`gap:${path.join(',')}:0`} data={{ type: 'gap', parentPath: path, itemIdx: 0 }} />
        </StyledItemList>
      )}

      {showContent && (ctx.hideSlides ? hasSubsections : items.length > 0) && (
        <StyledItemList $readOnly={ctx.readOnly}>
          {!ctx.readOnly && !ctx.hideSlides && (
            <DropGap
              id={`gap:${path.join(',')}:0`}
              data={{ type: 'gap', parentPath: path, itemIdx: 0 }}
              isActive={itemGapActive}
            />
          )}
          {items.map((item, idx) => (
            <React.Fragment key={idx}>
              {item.kind === 'slide' ? (
                !ctx.hideSlides && <SlideNode slide={item.data} parentPath={path} itemIdx={idx} />
              ) : (
                <SectionNode section={item.data} path={[...path, idx]} depth={depth + 1} />
              )}
              {!ctx.readOnly && !ctx.hideSlides && item.kind === 'section' && (
                <DropGap
                  id={`gap:${path.join(',')}:${idx + 1}`}
                  data={{ type: 'gap', parentPath: path, itemIdx: idx + 1 }}
                  isActive={itemGapActive}
                />
              )}
            </React.Fragment>
          ))}
        </StyledItemList>
      )}
    </StyledOutlineSection>
  );
};

interface OutlineProps {
  sections: PresentationOutlineSection[];
  maxDepth?: number;
  sectionLabel?: string;
  selectedSlides?: OutlineSlide[];
  activeSectionPath?: number[] | null;
  withDndContext?: boolean;
  forceExternalDrag?: boolean;
  hideDragOverlay?: boolean;
  defaultCollapsed?: boolean;
  onRename: (path: number[], newName: string) => void;
  onRemove: (path: number[]) => void;
  onToggle: (path: number[]) => void;
  onAddSection: () => void;
  onUpdateSlide: (sectionPath: number[], itemIdx: number, notes: string, searchQuery: string) => void;
  onRemoveSlide?: (sectionPath: number[], itemIdx: number) => void;
  onSlideSelect?: (data: OutlineSlideSelectData) => void;
  onReorder?: (sections: PresentationOutlineSection[]) => void;
  onDropExternal?: (sectionPath: number[], itemIdx: number, dragData: unknown) => void;
  onSectionSelect?: (path: number[], section: PresentationOutlineSection) => void;
  readOnly?: boolean;
  hideSlides?: boolean;
  hideReadingTime?: boolean;
  hideSlideCount?: boolean;
  compact?: boolean;
  showSectionToggle?: boolean;
  actionsRef?: React.RefObject<{ expandAll: () => void; collapseAll: () => void } | null>;
}

export type { OutlineSlideSelectData };

const collisionDetection = (args: Parameters<typeof pointerWithin>[0]) => {
  const pointer = pointerWithin(args);
  if (pointer.length > 0) return pointer;
  return closestCenter(args);
};

const OutlineContent: FC<OutlineProps> = ({
  sections,
  maxDepth = 1,
  sectionLabel = 'Section',
  selectedSlides = [],
  activeSectionPath = null,
  forceExternalDrag,
  hideDragOverlay,
  defaultCollapsed = false,
  hideSlides = false,
  hideReadingTime = false,
  hideSlideCount = false,
  compact = false,
  showSectionToggle = true,
  actionsRef,
  onRename,
  onRemove,
  onToggle,
  onAddSection,
  onUpdateSlide,
  onRemoveSlide,
  onSlideSelect,
  onReorder,
  onDropExternal,
  onSectionSelect,
  readOnly = false,
}) => {
  const dispatch = useDispatch();
  const collectSectionPaths = (sectionList: PresentationOutlineSection[]): string[] => {
    const paths: string[] = [];
    const collect = (section: PresentationOutlineSection, path: number[]) => {
      paths.push(path.join('-'));
      (section.items ?? []).forEach((item, idx) => {
        if (item.kind === 'section') collect(item.data, [...path, idx]);
      });
    };
    sectionList.forEach((section, i) => collect(section, [i]));
    return paths;
  };

  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    if (defaultCollapsed) return new Set();
    return new Set(collectSectionPaths(sections));
  });

  // Track every path we have ever seen so re-renders (e.g. after API refetch)
  // don't re-expand sections the user has manually collapsed.
  const seenPathsRef = useRef<Set<string>>(new Set(collectSectionPaths(sections)));

  const [editingPath, setEditingPath] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editingSlide, setEditingSlide] = useState<string | null>(null);
  const [editSlideNotes, setEditSlideNotes] = useState('');
  const [editSlideQuery, setEditSlideQuery] = useState('');
  const [activeDragData, setActiveDragData] = useState<DnDData | null>(null);
  const [slideDropTarget, setSlideDropTarget] = useState<{ overItemId: string; side: 'before' | 'after' } | null>(null);
  const slideDropSideRef = useRef<'before' | 'after'>('after');

  const onToggleSection = useCallback((path: number[]) => {
    const key = path.join('-');
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const onToggleSlide = useCallback((key: string) => {
    setExpandedSlides((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const onStartEditingSection = useCallback((path: number[], name: string) => {
    setEditingPath(path.join('-'));
    setEditValue(name);
  }, []);

  const onCommitEditSection = useCallback(() => {
    if (editingPath !== null && editValue.trim()) {
      onRename(editingPath.split('-').map(Number), editValue.trim());
    }
    setEditingPath(null);
  }, [editingPath, editValue, onRename]);

  const onCancelEditSection = useCallback(() => setEditingPath(null), []);
  const onRemoveSection = useCallback((path: number[]) => onRemove(path), [onRemove]);
  const onRemoveSlideItem = useCallback(
    (sectionPath: number[], itemIdx: number) => onRemoveSlide?.(sectionPath, itemIdx),
    [onRemoveSlide],
  );
  const onToggleEnabled = useCallback((path: number[]) => onToggle(path), [onToggle]);

  const onStartEditingSlide = useCallback((key: string, notes: string, query: string) => {
    setEditingSlide(key);
    setEditSlideNotes(notes);
    setEditSlideQuery(query);
    setExpandedSlides((prev) => new Set(prev).add(key));
  }, []);

  const onCommitSlideEdit = useCallback(
    (sectionPath: number[], itemIdx: number) => {
      onUpdateSlide(sectionPath, itemIdx, editSlideNotes, editSlideQuery);
      setEditingSlide(null);
      setEditSlideNotes('');
      setEditSlideQuery('');
    },
    [editSlideNotes, editSlideQuery, onUpdateSlide],
  );

  const onCancelEditingSlide = useCallback(() => {
    setEditingSlide(null);
    setEditSlideNotes('');
    setEditSlideQuery('');
  }, []);

  const isActiveSectionDrag = activeDragData?.type === 'section';
  const isActiveSlideDrag = activeDragData?.type === 'slide';
  const activePath = isActiveSectionDrag ? (activeDragData as SectionDragData).path : null;
  const activeSectionSubtreeHeight = activePath
    ? subtreeHeight(getAtPath(sections, activePath) ?? { kind: 'section', items: [] })
    : 0;

  const isSlideBeingDragged = useCallback(
    (parentPath: number[], itemIdx: number): boolean => {
      if (!isActiveSlideDrag || !activeDragData) return false;
      const { parentPath: fromParent, itemIdx: fromIdx } = activeDragData as SlideDragData;
      if (!fromParent) {
        return false;
      }
      const fromSection = getAtPath(sections, fromParent);
      const draggedItem = fromSection?.items?.[fromIdx];
      if (!draggedItem || draggedItem.kind !== 'slide') return false;
      const draggedSlide = draggedItem.data;

      const toSection = getAtPath(sections, parentPath);
      const toItem = toSection?.items?.[itemIdx];
      if (!toItem || toItem.kind !== 'slide') return false;

      const isDraggedSelected = selectedSlides.some(
        (selectedSlide) => selectedSlide.instanceId === draggedSlide.instanceId,
      );
      if (
        isDraggedSelected &&
        selectedSlides.some((selectedSlide) => selectedSlide.instanceId === toItem.data.instanceId)
      )
        return true;
      return parentPath.join(',') === fromParent.join(',') && itemIdx === fromIdx;
    },
    [activeDragData, isActiveSlideDrag, sections, selectedSlides],
  );

  useEffect(() => {
    if (defaultCollapsed) return;
    const paths = collectSectionPaths(sections);
    const newPaths = paths.filter((p) => !seenPathsRef.current.has(p));
    paths.forEach((p) => seenPathsRef.current.add(p));
    if (!newPaths.length) return;
    setExpandedSections((prev) => new Set([...prev, ...newPaths]));
  }, [sections, defaultCollapsed]);

  const [expandedSlides, setExpandedSlides] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!actionsRef) return;
    actionsRef.current = {
      expandAll: () => setExpandedSections(new Set(collectSectionPaths(sections))),
      collapseAll: () => setExpandedSections(new Set()),
    };
  }, [actionsRef, sections]);

  useDndMonitor({
    onDragStart: ({ active }: DragStartEvent) => {
      setActiveDragData((active.data.current as DnDData) ?? null);
    },
    onDragMove: ({ active, over }) => {
      if (!over || (over.data.current as DnDData)?.type !== 'slide-target') {
        setSlideDropTarget(null);
        return;
      }
      const translated = (active.rect.current as any)?.translated as { top: number; height: number } | null;
      const dragMidY = translated ? translated.top + translated.height / 2 : null;
      const overMidY = over.rect.top + over.rect.height / 2;
      const side: 'before' | 'after' = dragMidY !== null ? (dragMidY < overMidY ? 'before' : 'after') : 'after';
      slideDropSideRef.current = side;
      setSlideDropTarget({ overItemId: over.id as string, side });
    },
    onDragCancel: () => {
      setActiveDragData(null);
      setSlideDropTarget(null);
    },
    onDragEnd: ({ active, over }: DragEndEvent) => {
      setActiveDragData(null);
      setSlideDropTarget(null);
      if (!over) return;

      const dragData = active.data.current as DnDData;
      const dropData = over.data.current as DnDData;

      if (!dragData || !dropData) return;

      // gallery-slide → gap | sec-nest | slide-target
      if (
        dragData.type === 'gallery-slide' &&
        (dropData.type === 'gap' || dropData.type === 'sec-nest' || dropData.type === 'slide-target')
      ) {
        if (dropData.type === 'gap') {
          onDropExternal?.(dropData.parentPath, dropData.itemIdx, dragData.slideData);
        } else if (dropData.type === 'slide-target') {
          const side = slideDropSideRef.current;
          const toIdx = side === 'before' ? dropData.itemIdx : dropData.itemIdx + 1;
          onDropExternal?.(dropData.parentPath, toIdx, dragData.slideData);
        } else {
          const targetSection = getAtPath(sections, dropData.path);
          onDropExternal?.(dropData.path, targetSection?.items?.length ?? 0, dragData.slideData);
        }
        return;
      }

      if (!onReorder) return;

      // section → gap
      if (dragData.type === 'section' && dropData.type === 'gap') {
        const sourcePath = dragData.path;
        const { parentPath, itemIdx: gapIdx } = dropData;

        if (isAncestorOrSelf(sourcePath, parentPath)) return;

        const sourceSection = getAtPath(sections, sourcePath);
        if (!sourceSection) return;
        if (sourceSection.locked) return;
        if (parentPath.length + 1 + subtreeHeight(sourceSection) > maxDepth) {
          dispatch(
            addNotification({
              type: 'error',
              message: `Couldn't move "${sectionLabel.toLocaleLowerCase()}" — the maximum nesting depth (${maxDepth}) has been reached.`,
            }),
          );
          return;
        }

        let { result: newSections, removed } = removeAtPath(sections, sourcePath);
        if (!removed) return;

        // Adjust parentPath and gapIdx for the removal of the source
        const adjustedParentPath = shiftPathAfterRemoval(parentPath, sourcePath);
        const sameParent = sourcePath.slice(0, -1).join(',') === parentPath.join(',');
        const insertIdx = sameParent && sourcePath[sourcePath.length - 1] < gapIdx ? gapIdx - 1 : gapIdx;

        onReorder(insertAtPath(newSections, adjustedParentPath, insertIdx, removed));
        return;
      }

      // section → sec-nest (drop onto a section to make it a child)
      if (dragData.type === 'section' && dropData.type === 'sec-nest') {
        const sourcePath = dragData.path;
        const targetPath = dropData.path;

        if (isAncestorOrSelf(sourcePath, targetPath)) return;

        const sourceSection = getAtPath(sections, sourcePath);
        if (!sourceSection) return;
        if (sourceSection.locked) return;

        const targetSection = getAtPath(sections, targetPath);
        if (targetSection?.locked) return;
        if (targetPath.length + 1 + subtreeHeight(sourceSection) > maxDepth) {
          dispatch(
            addNotification({
              type: 'error',
              message: `Couldn't add sub-${sectionLabel.toLowerCase()} — the maximum nesting depth (${maxDepth}) has been reached.`,
            }),
          );
          return;
        }

        let { result: newSections, removed } = removeAtPath(sections, sourcePath);
        if (!removed) return;

        const adjustedTargetPath = shiftPathAfterRemoval(targetPath, sourcePath);
        newSections = updateAtPath(newSections, adjustedTargetPath, (section) => ({
          ...section,
          items: [...(section.items ?? []), { kind: 'section', data: removed! }],
        }));
        onReorder(newSections);
        return;
      }

      // slide → gap | sec-nest | slide-target
      if (
        dragData.type === 'slide' &&
        (dropData.type === 'gap' || dropData.type === 'sec-nest' || dropData.type === 'slide-target')
      ) {
        const { parentPath: fromParent, itemIdx: fromIdx } = dragData;
        let toParent: number[];
        let toIdx: number;
        if (dropData.type === 'gap') {
          toParent = dropData.parentPath;
          toIdx = dropData.itemIdx;
        } else if (dropData.type === 'slide-target') {
          const side = slideDropSideRef.current;
          toParent = dropData.parentPath;
          toIdx = side === 'before' ? dropData.itemIdx : dropData.itemIdx + 1;
        } else {
          toParent = dropData.path;
          toIdx = getAtPath(sections, dropData.path)?.items?.length ?? 0;
        }

        if (!fromParent) {
          return;
        }
        const fromSection = getAtPath(sections, fromParent);
        const draggedItem = fromSection?.items?.[fromIdx];
        if (!draggedItem || draggedItem.kind !== 'slide') return;
        const draggedSlide = draggedItem.data;

        const isDraggedSelected = selectedSlides.some(
          (selectedSlide) => selectedSlide.instanceId === draggedSlide.instanceId,
        );
        const slidesToMove: OutlineSlide[] = [];
        const removalPositions: Array<{ parentPath: number[]; itemIdx: number }> = [];

        if (isDraggedSelected && selectedSlides.length > 1) {
          const collect = (secs: PresentationOutlineSection[], parentPath: number[]) => {
            secs.forEach((section, sectionIdx) => {
              const sectionPath = [...parentPath, sectionIdx];
              (section.items ?? []).forEach((item, itemIdx) => {
                if (
                  item.kind === 'slide' &&
                  selectedSlides.some((selectedSlide) => selectedSlide.instanceId === item.data.instanceId)
                ) {
                  slidesToMove.push(item.data);
                  removalPositions.push({ parentPath: sectionPath, itemIdx });
                } else if (item.kind === 'section') {
                  collect([item.data], [...sectionPath, itemIdx]);
                }
              });
            });
          };
          collect(sections, []);
        } else {
          slidesToMove.push(draggedSlide);
          removalPositions.push({ parentPath: fromParent, itemIdx: fromIdx });
        }

        const newSections = deepCloneSections(sections);

        for (let i = removalPositions.length - 1; i >= 0; i--) {
          const { parentPath, itemIdx } = removalPositions[i];
          const section = getAtPath(newSections, parentPath);
          if (section) section.items.splice(itemIdx, 1);
        }

        const removedBeforeTarget = removalPositions.filter(
          (pos) => pos.parentPath.join(',') === toParent.join(',') && pos.itemIdx < toIdx,
        ).length;
        const adjustedInsertIdx = Math.max(0, toIdx - removedBeforeTarget);

        const targetSection = getAtPath(newSections, toParent);
        if (targetSection) {
          targetSection.items.splice(
            adjustedInsertIdx,
            0,
            ...slidesToMove.map((slide) => ({ kind: 'slide' as const, data: slide })),
          );
        }

        onReorder(newSections);
      }
    },
  });

  const getActiveLabel = (): string => {
    if (!activeDragData) return '';
    if (activeDragData.type === 'section') {
      const section = getAtPath(sections, activeDragData.path);
      return section ? section.displayName || section.title || sectionLabel : sectionLabel;
    }
    if (activeDragData.type === 'slide') {
      const { parentPath, itemIdx } = activeDragData;
      if (!parentPath) {
        return 'Slide';
      }
      const item = getAtPath(sections, parentPath)?.items?.[itemIdx];
      const slide = item?.kind === 'slide' ? item.data : null;
      const isDraggedSelected =
        slide && selectedSlides.some((selectedSlide) => selectedSlide.instanceId === slide.instanceId);
      if (isDraggedSelected && selectedSlides.length > 1) return `Moving ${selectedSlides.length} slides`;
      return slide ? slide.Title || 'Slide' : 'Slide';
    }
    if (activeDragData.type === 'gallery-slide') {
      const slideData = activeDragData.slideData as any;
      return slideData?.title || slideData?.name || 'Slide';
    }
    return '';
  };

  const isExternalDrag =
    activeDragData?.type === 'gallery-slide' || !!(activeDragData as any)?.item || (forceExternalDrag ?? false);

  const ctxValue: OutlineCtxValue = {
    sections,
    maxDepth,
    sectionLabel,
    isActiveSectionDrag,
    isActiveSlideDrag,
    isExternalDrag,
    activePath,
    activeSectionSubtreeHeight,
    expandedSections,
    expandedSlides,
    onToggleSection,
    onToggleSlide,
    editingPath,
    editValue,
    setEditValue,
    onStartEditingSection,
    onCommitEditSection,
    onCancelEditSection,
    onRemoveSection,
    onRemoveSlide: onRemoveSlideItem,
    onToggleEnabled,
    editingSlide,
    editSlideNotes,
    editSlideQuery,
    setEditSlideNotes,
    setEditSlideQuery,
    onStartEditingSlide,
    onCommitSlideEdit,
    onCancelEditingSlide,
    selectedSlides,
    activeSectionPath,
    onSlideSelect,
    onSectionSelect,
    isSlideBeingDragged,
    readOnly,
    hideSlides,
    hideReadingTime,
    hideSlideCount,
    compact,
    showSectionToggle,
    slideDropTarget,
  };

  return (
    <OutlineCtx.Provider value={ctxValue}>
      <DropGap id="root-gap:0" data={{ type: 'gap', parentPath: [], itemIdx: 0 }} isActive={isActiveSectionDrag} />

      {sections.map((section, index) => (
        <React.Fragment key={index}>
          <SectionNode section={section} path={[index]} depth={0} />
          <DropGap
            id={`root-gap:${index + 1}`}
            data={{ type: 'gap', parentPath: [], itemIdx: index + 1 }}
            isActive={isActiveSectionDrag}
          />
        </React.Fragment>
      ))}

      {!readOnly && (
        <StyledAddSectionButton onClick={onAddSection}>
          <AddIcon fontSize="small" /> Add New {sectionLabel}
        </StyledAddSectionButton>
      )}

      {!hideDragOverlay && (
        <DragOverlay dropAnimation={null}>
          {activeDragData && <StyledDragOverlayBox>{getActiveLabel()}</StyledDragOverlayBox>}
        </DragOverlay>
      )}
    </OutlineCtx.Provider>
  );
};

export const PresentationOutline: FC<OutlineProps> = ({ withDndContext = true, ...props }) => {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  if (!withDndContext) {
    return <OutlineContent {...props} hideDragOverlay />;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={collisionDetection}>
      <OutlineContent {...props} />
    </DndContext>
  );
};
